import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import isEmpty from 'lodash.isempty';
import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getEntitlementList} from 'src/modules/entitlements/utils/getEntitlementList';
import {getGrantsByIds} from 'src/openapi/admin/apis/EntitlementsGrantApi';
import {getResourcesByIds} from 'src/openapi/admin/apis/EntitlementsResourceApi';
import {getRoleNamesByIds} from 'src/openapi/admin/apis/EntitlementsRoleApi';
import {searchEntitlements} from 'src/openapi/admin/apis/EntitlementsSearchApi';
import {ResourceType} from 'src/openapi/admin/models/ResourceType';
import {useAppDispatch} from 'src/store/hooks';

import {useAbortSignal} from '@pb/kit-hooks-use-abort-signal';

import {
    rolesGrantsResourcesStateSelector,
} from '../slices/roles-grants-resources-state.slice';
import {
    rolesGrantsResourcesActions,
    rolesGrantsResourcesDataSelector,
    rolesGrantsResourcesResultSelector,
} from '../slices/roles-grants-resources.slice';

import type {RolesGrantsResourcesFilters} from '../typedef';
import type {CacheKeys} from 'src/hooks/common-data-service/cached-table';
import type {EntitlementResource} from 'src/openapi/admin/models/EntitlementResource';
import type {EntitlementsSearch} from 'src/openapi/admin/models/EntitlementsSearch';
import type {Grant} from 'src/openapi/admin/models/Grant';
import type {Role} from 'src/openapi/admin/models/Role';


type GetRolesGrantsResourcesCacheKeys = CacheKeys<undefined, undefined, RolesGrantsResourcesFilters>;

export const useGetRolesGrantsResources = () => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();
    const {createAbortSignal} = useAbortSignal();

    const getAction = useCallback((cacheKeys: GetRolesGrantsResourcesCacheKeys) => apiDispatch(searchEntitlements({
        pageNumber: cacheKeys.pagination.currentPage,
        pageSize: cacheKeys.pagination.pageSize,
        sortDirection: cacheKeys.sortCriteria.direction,
        excludedResourceTypes: [ResourceType.DATASET],
        requestBody: cacheKeys.filters?.resourceIds as (string[] | undefined),
    }, {signal: createAbortSignal()})), [apiDispatch, createAbortSignal]);

    const setLoadedData = useCallback(async (
        response: EntitlementsSearch,
        cacheKeys: GetRolesGrantsResourcesCacheKeys,
    ) => {
        const filteredResources = cacheKeys.filters.resourceIds as string[] | undefined;
        const relations = filteredResources && !filteredResources.length ? [] : response.items || [];
        const signal = createAbortSignal();

        let roles: Record<string, Role> = {};
        const roleIds = [...new Set(relations
            .filter(({roleId}) => roleId)
            .map(({roleId}) => roleId || ''),
        )];
        const rolesResponse = await apiDispatch(getRoleNamesByIds(
            {requestBody: roleIds},
            {signal},
        ));
        if (rolesResponse.payload.success) {
            roles = rolesResponse.payload.data;
        } else {
            dispatch(rolesGrantsResourcesActions.onFailure(rolesResponse.payload.data));
            return;
        }

        let grants: Record<string, Grant> = {};
        const grantIds = [...new Set(relations
            .map(({grantId}) => grantId),
        )];
        const grantsResponse = await apiDispatch(getGrantsByIds(
            {requestBody: grantIds},
            {signal},
        ));
        if (grantsResponse.payload.success) {
            grants = grantsResponse.payload.data;
        } else {
            dispatch(rolesGrantsResourcesActions.onFailure(grantsResponse.payload.data));
            return;
        }

        let resources: Record<string, EntitlementResource> = {};
        const resourceIds = [...new Set(relations
            .map(({resourceId}) => resourceId),
        )];
        const resourcesResponse = await apiDispatch(getResourcesByIds(
            {requestBody: resourceIds},
            {signal},
        ));
        if (resourcesResponse.payload.success) {
            resources = resourcesResponse.payload.data;
        } else {
            dispatch(rolesGrantsResourcesActions.onFailure(resourcesResponse.payload.data));
            return;
        }

        const data = relations.length && !isEmpty(grants) && !isEmpty(resources)
            ? getEntitlementList({
                    roles,
                    grants,
                    resources,
                    relations,
                    sortDirection: cacheKeys.sortCriteria.direction,
                }) : [];

        dispatch(rolesGrantsResourcesActions.onSuccess({
            cacheKeys,
            totalCount: response.totalCount,
            data,
        }));
    }, [apiDispatch, createAbortSignal, dispatch]);

    return useCachedTable({
        dataSelector: rolesGrantsResourcesDataSelector,
        stateSelector: rolesGrantsResourcesStateSelector,
        resultSelector: rolesGrantsResourcesResultSelector,
        actions: rolesGrantsResourcesActions,
        getAction,
        onSuccessLoad: setLoadedData,
    });
};
